import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2" style={{ color: 'var(--fy-text-secondary)' }} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden" style={{ backgroundColor: 'var(--fy-background-secondary)' }}>
              <img src="https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png" alt="LikeKind" className="w-8 h-8 object-cover" />
            </div>
            <span className="font-bold" style={{ color: 'var(--fy-text-primary)' }}>LikeKind</span>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <div className="flex items-center gap-3 mb-10">
          <Shield className="w-8 h-8" style={{ color: 'var(--lk-sage)' }} />
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ color: 'var(--fy-text-primary)', fontFamily: 'Outfit' }} data-testid="page-title">
            Privacy Policy
          </h1>
        </div>

        <div className="space-y-10">

          {/* What we collect — lead with facts */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>What We Collect</h2>
            <ul className="space-y-2">
              {[
                'Account information (email, name)',
                'Activity participation (for coordination)',
                'Basic technical data (device, errors, security)',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3" style={{ color: 'var(--fy-text-secondary)' }}>
                  <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--lk-sage)' }}></span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-4 font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              We do not collect precise location data.
            </p>
          </section>

          {/* How it's used — positive framing first */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>How It's Used</h2>
            <p className="mb-3" style={{ color: 'var(--fy-text-secondary)' }}>Information is used to:</p>
            <ul className="space-y-2 mb-4">
              {[
                'Run the platform',
                'Support activity coordination',
                'Maintain system reliability',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3" style={{ color: 'var(--fy-text-secondary)' }}>
                  <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--lk-sage)' }}></span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-sm" style={{ color: 'var(--fy-text-disabled)' }}>
              Not used for engagement optimization, content ranking, or behavioral targeting.
            </p>
          </section>

          {/* What we don't do — brief, one block */}
          <section className="p-5 rounded-xl" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
            <p className="mb-2 font-medium" style={{ color: 'var(--fy-text-primary)' }}>We do not:</p>
            <ul className="space-y-1.5 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
              {[
                'Sell personal data',
                'Use behavioral advertising',
                'Track users across apps or websites',
                'Build behavioral or psychological profiles',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--fy-text-disabled)' }}></span>
                  {item}
                </li>
              ))}
            </ul>
          </section>

          {/* Children */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>Children & Families</h2>
            <ul className="space-y-2">
              {[
                'Children under 13 require parent accounts',
                'Parent visibility is built into the system',
                'No behavioral tracking or advertising for minors',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3" style={{ color: 'var(--fy-text-secondary)' }}>
                  <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--lk-sage)' }}></span>
                  {item}
                </li>
              ))}
            </ul>
          </section>

          {/* Principle */}
          <section className="p-6 rounded-xl" style={{ backgroundColor: 'var(--lk-sage-light)', border: '1px solid var(--lk-sage)' }}>
            <p className="leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>Privacy is not a feature.</p>
            <p className="mt-2 leading-relaxed font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              It is the baseline for how LikeKind operates.
            </p>
          </section>

          {/* Contact + Related */}
          <section className="pt-6" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
            <p className="mb-4 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
              Contact: <a href="mailto:privacy@likekind.com" className="underline" style={{ color: 'var(--lk-sage)' }}>privacy@likekind.com</a>
            </p>
            <div className="flex flex-wrap gap-3">
              {[
                { to: '/trust', label: 'Trust & Safety' },
                { to: '/parent-visibility', label: 'Parent Visibility' },
                { to: '/terms', label: 'Terms of Service' },
              ].map(link => (
                <Link key={link.to} to={link.to} className="px-4 py-2 rounded-full text-sm" style={{ backgroundColor: 'var(--fy-background-secondary)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}>
                  {link.label}
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>

      <footer className="mt-16 py-8" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p style={{ color: 'var(--fy-text-disabled)' }}>© {new Date().getFullYear()} LikeKind Inc.</p>
        </div>
      </footer>
    </div>
  );
}
